import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Chip,
  FormControlLabel,
  Checkbox,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  OutlinedInput,
  ListItemText,
  Alert,
} from '@mui/material';
import { Warning as WarningIcon, Error as ErrorIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTenant } from '../context/TenantContext';
import { taskService } from '../services/taskService';
import { Task } from '../types/task';
import { formatDateTime } from '../utils/dateUtils';
import { taskColumns } from '../config/taskColumns';
import useTablePage from '../components/table/hooks/useTablePage';
import DataTableToolbar from '../components/table/DataTableToolbar';
import DataTableContainer from '../components/table/DataTableContainer';
import DataTableBody from '../components/table/DataTableBody';
import TablePageLayout from '../components/table/TablePageLayout';
import ManageColumnsModal from '../components/modals/ManageColumnsModal';
import SaveViewModal from '../components/modals/SaveViewModal';
import { useTaskActions } from '../components/tasks/useTaskActions';

const SEVERITY_COLORS: Record<string, 'error' | 'warning'> = {
  ERROR: 'error',
  WARNING: 'warning',
};

const formatTypeLabel = (type: string) =>
  type.split('_').map(w => w.charAt(0) + w.slice(1).toLowerCase()).join(' ');

const TasksPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { currentTenant } = useTenant();

  // Users without all_patients only ever receive their own tasks from the
  // server, so the Owner / Only Mine filters are redundant and hidden.
  const hasAllPatients = !!user?.user_privilege?.all_patients;

  const [onlyMine, setOnlyMine] = useState(false);
  const [typeFilter, setTypeFilter] = useState<string[]>([]);
  const [severityFilter, setSeverityFilter] = useState<string[]>([]);
  const [areaFilter, setAreaFilter] = useState<string[]>([]);
  const [ownerFilter, setOwnerFilter] = useState<string[]>([]);


  const filters = useMemo(() => {
    const f: Record<string, any> = {};
    if (onlyMine && user) f.owner_id = user.id;
    if (typeFilter.length) f.type = typeFilter;
    if (severityFilter.length) f.severity = severityFilter;
    if (areaFilter.length) f.area = areaFilter;
    return f;
  }, [onlyMine, user, typeFilter, severityFilter, areaFilter]);

  const getSortValue = useCallback((task: Task, key: string): any => {
    switch (key) {
      case 'severity': return task.severity;
      case 'type': return task.type;
      case 'patient': return task.patient_name.toLowerCase();
      case 'meeting': return (task.meeting_scheduled_at || '');
      case 'owner': return (task.owner_name || 'secretary').toLowerCase();
      case 'area': return task.area;
      case 'date': return task.date;
      case 'hmId': return (task.hm_id || '').toLowerCase();
      case 'clinic': return (task.clinic_name || '').toLowerCase();
      case 'nextMeetingStart': return task.next_meeting_start || '';
      case 'nextMeetingType': return (task.next_meeting_type_name || '').toLowerCase();
      default: return '';
    }
  }, []);

  const getSearchText = useCallback((task: Task): string[] => [
    task.patient_name,
    task.owner_name || 'secretary',
    task.type,
    task.area,
    task.hm_id || '',
    task.clinic_name || '',
    task.meeting_type_name || '',
    task.next_meeting_type_name || '',
  ], []);

  const table = useTablePage<Task>({
    entityType: 'tasks',
    columns: taskColumns,
    tenantId: currentTenant?.id,
    getSortValue,
    getSearchText,
    filters,
  });

  useEffect(() => {
    if (!currentTenant?.id) return;
    table.loadData(() => taskService.getTasks(currentTenant.id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTenant?.id]);

  const { openTask, dialogs: taskActionDialogs } = useTaskActions({
    onChanged: () => { if (currentTenant?.id) table.loadData(() => taskService.getTasks(currentTenant.id)); },
  });

  const availableTypes = useMemo(() => {
    const seen = new Set<string>();
    return table.data
      .map(t => t.type)
      .filter(type => { if (seen.has(type)) return false; seen.add(type); return true; })
      .sort();
  }, [table.data]);

  const availableOwners = useMemo(() => {
    const seen = new Set<string>();
    return table.data
      .map(t => t.owner_name || 'Secretary')
      .filter(name => { if (seen.has(name)) return false; seen.add(name); return true; })
      .sort((a, b) => a === 'Secretary' ? 1 : b === 'Secretary' ? -1 : a.localeCompare(b));
  }, [table.data]);

  const filteredData = useMemo(() => {
    if (!ownerFilter.length) return table.sortedData;
    return table.sortedData.filter(t => ownerFilter.includes(t.owner_name || 'Secretary'));
  }, [table.sortedData, ownerFilter]);

  const handleTypeClick = useCallback((e: React.MouseEvent, task: Task) => {
    e.stopPropagation();
    openTask(task);
  }, [openTask]);

  const linkSx = { color: 'primary.main', textDecoration: 'underline', cursor: 'pointer' };

  const getColumnValue = (task: Task, columnKey: string): React.ReactNode => {
    switch (columnKey) {
      case 'severity':
        return (
          <Chip
            icon={task.severity === 'ERROR' ? <ErrorIcon /> : <WarningIcon />}
            label={task.severity === 'ERROR' ? 'Error' : 'Warning'}
            color={SEVERITY_COLORS[task.severity] || 'default'}
            size="small"
          />
        );
      case 'type': return (
        <Box component="span" sx={linkSx} onClick={(e) => handleTypeClick(e, task)}>
          {formatTypeLabel(task.type)}
        </Box>
      );
      case 'patient': return (
        <Box component="span" sx={linkSx}
          onClick={(e) => { e.stopPropagation(); navigate(`/patients/${task.patient_id}`); }}>
          {task.patient_name}
        </Box>
      );
      case 'meeting': {
        const meetingText = task.meeting_scheduled_at
          ? `${task.meeting_type_name || ''} ${formatDateTime(task.meeting_scheduled_at)}`.trim()
          : '-';
        return task.meeting_scheduled_at && task.meeting_id ? (
          <Box component="span" sx={linkSx}
            onClick={(e) => { e.stopPropagation(); navigate(`/patients/${task.patient_id}?meeting_id=${task.meeting_id}`); }}>
            {meetingText}
          </Box>
        ) : meetingText;
      }
      case 'owner': return task.owner_name || 'Secretary';
      case 'area': return task.area === 'CLINIC' ? 'Clinic' : 'Admin';
      case 'date': return formatDateTime(task.date);
      case 'hmId': return task.hm_id ? (
        <Box component="span" sx={linkSx}
          onClick={(e) => { e.stopPropagation(); navigate(`/patients/${task.patient_id}`); }}>
          {task.hm_id}
        </Box>
      ) : '-';
      case 'clinic': return task.clinic_name || '-';
      case 'nextMeetingStart': return task.next_meeting_start ? formatDateTime(task.next_meeting_start) : '-';
      case 'nextMeetingType': return task.next_meeting_type_name || '-';
      default: return '';
    }
  };

  return (
    <TablePageLayout loading={false} error={null}>
      <DataTableToolbar
        {...table.toolbarProps}
        title="Tasks"
        searchPlaceholder="Search tasks..."
        isAdmin={!!user?.user_privilege?.config}
        actions={
          <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
            {hasAllPatients && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={onlyMine}
                    onChange={(e) => setOnlyMine(e.target.checked)}
                    size="small"
                  />
                }
                label="Only Mine"
              />
            )}
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel id="task-type-filter-label">Type</InputLabel>
              <Select
                labelId="task-type-filter-label"
                multiple
                value={typeFilter}
                onChange={(e) => {
                  const v = e.target.value;
                  setTypeFilter(typeof v === 'string' ? v.split(',') : v as string[]);
                }}
                input={<OutlinedInput label="Type" />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {(selected as string[]).map(v => <Chip key={v} label={formatTypeLabel(v)} size="small" />)}
                  </Box>
                )}
              >
                {availableTypes.map(t => (
                  <MenuItem key={t} value={t}>
                    <Checkbox checked={typeFilter.includes(t)} />
                    <ListItemText primary={formatTypeLabel(t)} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel id="task-severity-filter-label">Severity</InputLabel>
              <Select
                labelId="task-severity-filter-label"
                multiple
                value={severityFilter}
                onChange={(e) => {
                  const v = e.target.value;
                  setSeverityFilter(typeof v === 'string' ? v.split(',') : v as string[]);
                }}
                input={<OutlinedInput label="Severity" />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {(selected as string[]).map(v => <Chip key={v} label={v === 'ERROR' ? 'Error' : 'Warning'} size="small" />)}
                  </Box>
                )}
              >
                {['ERROR', 'WARNING'].map(s => (
                  <MenuItem key={s} value={s}>
                    <Checkbox checked={severityFilter.includes(s)} />
                    <ListItemText primary={s === 'ERROR' ? 'Error' : 'Warning'} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 130 }}>
              <InputLabel id="task-area-filter-label">Area</InputLabel>
              <Select
                labelId="task-area-filter-label"
                multiple
                value={areaFilter}
                onChange={(e) => {
                  const v = e.target.value;
                  setAreaFilter(typeof v === 'string' ? v.split(',') : v as string[]);
                }}
                input={<OutlinedInput label="Area" />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {(selected as string[]).map(v => <Chip key={v} label={v === 'CLINIC' ? 'Clinic' : 'Admin'} size="small" />)}
                  </Box>
                )}
              >
                {['CLINIC', 'ADMIN'].map(a => (
                  <MenuItem key={a} value={a}>
                    <Checkbox checked={areaFilter.includes(a)} />
                    <ListItemText primary={a === 'CLINIC' ? 'Clinic' : 'Admin'} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {hasAllPatients && (
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel id="task-owner-filter-label">Owner</InputLabel>
                <Select
                  labelId="task-owner-filter-label"
                  multiple
                  value={ownerFilter}
                  onChange={(e) => {
                    const v = e.target.value;
                    setOwnerFilter(typeof v === 'string' ? v.split(',') : v as string[]);
                  }}
                  input={<OutlinedInput label="Owner" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {(selected as string[]).map(v => <Chip key={v} label={v} size="small" />)}
                    </Box>
                  )}
                >
                  {availableOwners.map(name => (
                    <MenuItem key={name} value={name}>
                      <Checkbox checked={ownerFilter.includes(name)} />
                      <ListItemText primary={name} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>
        }
      />

      {table.error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {table.error}
        </Alert>
      )}

      {table.loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <Typography color="text.secondary">Loading...</Typography>
        </Box>
      ) : (
        <DataTableContainer {...table.containerProps}>
          <DataTableBody
            data={filteredData}
            {...table.tableBodyProps}
            getColumnValue={getColumnValue}
            getRowKey={(task) => task.logical_id}
          />
        </DataTableContainer>
      )}

      <ManageColumnsModal {...table.manageColumnsProps} />
      <SaveViewModal {...table.saveViewModalProps} isAdmin={!!user?.user_privilege?.config} />

      {taskActionDialogs}
    </TablePageLayout>
  );
};

export default TasksPage;
